import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiReports, { IOfferDashboard, IOfferDashboardOffer } from 'api/reports';
import PageContainer from 'components/layout/PageContainer';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import { useReportCurrency } from 'services/offers/exchangeRate';
import translations from 'services/translations/translations';
import CurrencyToggle from 'components/offers/src/CurrencyToggle';
import { isOrderedStatus } from 'services/enums/offers/Statuses';

import {
  BarList,
  Donut,
  Funnel,
  ISeries,
  PALETTE,
  StackedColumns,
} from './DashboardCharts';
import { daysOf, monthsOf } from './reportPeriods';
import useReportPeriod from './ReportPeriodPicker';

import 'style/reports/OfferDashboard.css';

// The colors of the statuses, the same in every chart and in the table
const STATUS_COLORS: { [status: string]: string } = {
  open: '#56ccf2',
  'ready-to-send': '#f2c94c',
  sent: '#2f80ed',
  approved: '#27ae60',
  'ordered-as-material': '#6fcf97',
  rejected: '#eb5757',
  'not-realized': '#b8c0c8',
  lost: '#f2994a',
  'converted-preliminary-offer': '#9b51e0',
  'converted-offer': '#6c3fb5',
};

const STATUS_ORDER = Object.keys(STATUS_COLORS);

const TYPE_ORDER = [
  'opportunity',
  'preliminary-offer',
  'offer',
  'material-offer',
  'cost-estimate',
];

// The margin % marks of the calculation window: under 20 % red, over 30 % green
const getMarginColor = (ratio: null | number): string => {
  if (ratio === null) {
    return '#b8c0c8';
  }

  if (ratio < 20) {
    return '#eb5757';
  }

  return ratio > 30 ? '#27ae60' : '#e2b93b';
};

const getMarginRatio = (value: number, expense: number): null | number =>
  value > 0 ? ((value - expense) / value) * 100 : null;

// Money shortly for the charts, e.g. "1,2 M€", "350 t€" or "1,2 MSEK"
const shortMoney = (value: number, symbol: string): string => {
  if (Math.abs(value) >= 1000000) {
    return `${formatGroupedNumber(value / 1000000, 1, 1)} M${symbol}`;
  }

  if (Math.abs(value) >= 10000) {
    return `${formatGroupedNumber(value / 1000, 0, 0)} t${symbol}`;
  }

  return `${formatGroupedNumber(value, 0, 0)} ${symbol}`;
};

const money = (value: null | number | undefined, symbol: string): string =>
  value === null || value === undefined
    ? '–'
    : `${formatGroupedNumber(value, 0, 0)} ${symbol}`;

// The sum of an offer in the dashboard: the estimate of an opportunity, else the offer
const getAmount = (offer: IOfferDashboardOffer): number =>
  (offer.type === 'opportunity' ? offer.estimate : offer.value) ?? 0;

type SortKey =
  | 'created'
  | 'customerName'
  | 'departmentName'
  | 'margin'
  | 'status'
  | 'targetName'
  | 'type'
  | 'amount';

// A period of at most this many days is shown by day, a longer one by month
const MAX_DAY_COLUMNS = 31;

const OfferDashboard = ({
  tabs,
}: {
  // The tabs of the offer reports, above the title
  tabs?: JSX.Element;
}): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);
  const tr = (key: string, options?: any) =>
    t(`${translations.offers}:dashboard.${key}`, options);

  const { period, picker } = useReportPeriod('offer-dashboard');
  const [data, setData] = useState<IOfferDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [department, setDepartment] = useState<null | string>(null);
  const [type, setType] = useState<null | string>(null);
  const [status, setStatus] = useState<null | string>(null);
  const [measure, setMeasure] = useState<'amount' | 'count'>('amount');
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortAscending, setSortAscending] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    apiReports
      .getOfferDashboard(period.from, period.to)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [period]);

  // The sums in one currency: euros, or kronas when only Swedish offers are viewed (e.g. a
  // Swedish department chosen); the offers of the other currency at the rate of today
  const report = useReportCurrency(
    (data?.offers ?? [])
      .filter(
        (offer) =>
          department === null ||
          (offer.departmentName ?? tr('No department')) === department
      )
      .map((offer) => offer.country)
  );
  const shortEuros = (value: number) => shortMoney(value, report.symbol);
  const euros = (value: null | number | undefined) =>
    money(value, report.symbol);

  // Every offer of the period is shown, its sums in the currency shown
  const offers = useMemo(
    () =>
      (data?.offers ?? []).map((offer) => {
        const convert = (value: null | number) =>
          value === null ? null : report.convertFrom(offer.country, value);
        return {
          ...offer,
          estimate: convert(offer.estimate),
          expense: convert(offer.expense),
          value: convert(offer.value),
        };
      }),
    [data, report.currency, report.rate]
  );
  const offerCountries = useMemo(() => {
    const countries = new Map<number, null | string | undefined>();
    (data?.offers ?? []).forEach((offer) =>
      countries.set(offer.id, offer.country)
    );
    return countries;
  }, [data]);

  const departments = useMemo(
    () =>
      Array.from(
        new Set(
          offers.map((offer) => offer.departmentName ?? tr('No department'))
        )
      ).sort(),
    [offers]
  );

  const departmentColors = useMemo(() => {
    const colors: { [name: string]: string } = {};
    departments.forEach((name, index) => {
      colors[name] = PALETTE[index % PALETTE.length];
    });
    return colors;
  }, [departments]);

  const departmentOf = (offer: IOfferDashboardOffer) =>
    offer.departmentName ?? tr('No department');

  // The offers chosen with the filters (each chart filters by the others)
  const filtered = useMemo(
    () =>
      offers.filter(
        (offer) =>
          (department === null || departmentOf(offer) === department) &&
          (type === null || offer.type === type) &&
          (status === null || offer.status === status)
      ),
    [offers, department, type, status]
  );

  const realOffers = filtered.filter((offer) => offer.type !== 'opportunity');
  const opportunities = filtered.filter(
    (offer) => offer.type === 'opportunity'
  );

  const offeredValue = realOffers.reduce(
    (total, offer) => total + (offer.value ?? 0),
    0
  );
  // The margin of all offers (opportunities have no calculation)
  const marginOffers = realOffers;
  const marginValue = marginOffers.reduce(
    (total, offer) => total + (offer.value ?? 0),
    0
  );
  const marginExpense = marginOffers.reduce(
    (total, offer) => total + (offer.expense ?? 0),
    0
  );
  const marginRatio = getMarginRatio(marginValue, marginExpense);

  // An approved preliminary offer was converted into an offer, it is not yet a deal
  const deals = realOffers.filter(
    (offer) => offer.type !== 'preliminary-offer'
  );
  // Ordered as a whole or as material
  const approved = deals.filter((offer) => isOrderedStatus(offer.status));
  const rejected = deals.filter((offer) => offer.status === 'rejected');
  const approvedValue = approved.reduce(
    (total, offer) => total + (offer.value ?? 0),
    0
  );
  const winRate =
    approved.length + rejected.length > 0
      ? (approved.length / (approved.length + rejected.length)) * 100
      : null;

  const openOffers = realOffers.filter(
    (offer) =>
      offer.status === 'open' ||
      offer.status === 'ready-to-send' ||
      offer.status === 'sent'
  );
  const openOpportunities = opportunities.filter(
    (offer) => offer.status === 'open'
  );
  const pipeline =
    openOffers.reduce((total, offer) => total + (offer.value ?? 0), 0) +
    openOpportunities.reduce(
      (total, offer) => total + (offer.estimate ?? 0),
      0
    );

  const measureOf = (offer: IOfferDashboardOffer) =>
    measure === 'amount' ? getAmount(offer) : 1;
  const formatMeasure = (value: number) =>
    measure === 'amount'
      ? shortEuros(value)
      : `${formatGroupedNumber(value, 0, 0)}`;

  // The days of a short period or the months of a longer one, by status
  const days = useMemo(() => daysOf(period), [period]);
  const byDay = days.length <= MAX_DAY_COLUMNS;
  const monthColumns = useMemo(() => {
    if (data === null) {
      return [];
    }

    const starts = byDay ? days : monthsOf(period);

    return starts.map((start) => {
      const values: { [status: string]: number } = {};

      filtered
        .filter((offer) => {
          const created = new Date(offer.created);
          return (
            created.getFullYear() === start.getFullYear() &&
            created.getMonth() === start.getMonth() &&
            (!byDay || created.getDate() === start.getDate())
          );
        })
        .forEach((offer) => {
          values[offer.status] = (values[offer.status] ?? 0) + measureOf(offer);
        });

      return {
        label: byDay
          ? `${start.getDate()}.${start.getMonth() + 1}.`
          : `${start.getMonth() + 1}/${`${start.getFullYear()}`.slice(2)}`,
        values,
      };
    });
  }, [data, filtered, period, days, byDay, measure]);

  const statusSeries: ISeries[] = STATUS_ORDER.filter((key) =>
    offers.some((offer) => offer.status === key)
  ).map((key) => ({
    color: STATUS_COLORS[key],
    key,
    label: t(`${translations.offers}:statuses.${key}`),
  }));

  // Status filter is not applied to the status donut itself, so all parts stay visible
  const forStatusDonut = offers.filter(
    (offer) =>
      (department === null || departmentOf(offer) === department) &&
      (type === null || offer.type === type)
  );
  const statusParts = statusSeries.map((item) => {
    const ofStatus = forStatusDonut.filter(
      (offer) => offer.status === item.key
    );
    const value = ofStatus.reduce(
      (total, offer) => total + measureOf(offer),
      0
    );
    return {
      color: item.color,
      key: item.key,
      label: item.label,
      text:
        measure === 'amount'
          ? shortEuros(value)
          : `${ofStatus.length} ${tr('pcs')}`,
      value,
    };
  });

  const departmentBars = departments
    .map((name) => {
      const ofDepartment = offers.filter(
        (offer) =>
          departmentOf(offer) === name &&
          (type === null || offer.type === type) &&
          (status === null || offer.status === status)
      );
      const value = ofDepartment.reduce(
        (total, offer) => total + measureOf(offer),
        0
      );
      const forMargin = ofDepartment.filter(
        (offer) => offer.type !== 'opportunity'
      );
      const offerValue = forMargin.reduce(
        (total, offer) => total + (offer.value ?? 0),
        0
      );
      const offerExpense = forMargin.reduce(
        (total, offer) => total + (offer.expense ?? 0),
        0
      );
      const ratio = getMarginRatio(offerValue, offerExpense);

      return {
        badge: {
          color: getMarginColor(ratio),
          text: ratio === null ? '–' : `${formatGroupedNumber(ratio, 0, 0)} %`,
        },
        color: departmentColors[name],
        key: name,
        label: name,
        text: formatMeasure(value),
        value,
      };
    })
    .filter((bar) => bar.value > 0)
    .sort((a, b) => b.value - a.value);

  // The work types of the positions of the chosen offers
  const filteredIds = new Set(realOffers.map((offer) => offer.id));
  const marginIds = new Set(marginOffers.map((offer) => offer.id));
  const workTypeTotals: {
    [group: string]: {
      expense: number;
      marginValue: number;
      value: number;
    };
  } = {};
  (data?.groups ?? [])
    .filter((group) => filteredIds.has(group.offerId))
    .map((group) => ({
      ...group,
      expense: report.convertFrom(
        offerCountries.get(group.offerId),
        group.expense
      ),
      value: report.convertFrom(offerCountries.get(group.offerId), group.value),
    }))
    .forEach((group) => {
      const total = workTypeTotals[group.group] ?? {
        expense: 0,
        marginValue: 0,
        value: 0,
      };
      const forMargin = marginIds.has(group.offerId);
      workTypeTotals[group.group] = {
        expense: total.expense + (forMargin ? group.expense : 0),
        marginValue: total.marginValue + (forMargin ? group.value : 0),
        value: total.value + group.value,
      };
    });
  const workTypeBars = Object.keys(workTypeTotals)
    .map((group, index) => {
      const ratio = getMarginRatio(
        workTypeTotals[group].marginValue,
        workTypeTotals[group].expense
      );
      return {
        badge: {
          color: getMarginColor(ratio),
          text: ratio === null ? '–' : `${formatGroupedNumber(ratio, 0, 0)} %`,
        },
        color: PALETTE[(index + 3) % PALETTE.length],
        key: group,
        label: t(`${translations.products}:groups:${group}`),
        text: shortEuros(workTypeTotals[group].value),
        value: workTypeTotals[group].value,
      };
    })
    .sort((a, b) => b.value - a.value);

  // The steps of selling of the chosen offers
  const ofType = (offerType: string) =>
    filtered.filter((offer) => offer.type === offerType);
  const funnelSteps = [
    {
      label: tr('Opportunities'),
      list: ofType('opportunity'),
      color: '#56ccf2',
    },
    {
      label: tr('Preliminary offers'),
      list: ofType('preliminary-offer'),
      color: '#9b51e0',
    },
    { label: tr('Offers'), list: ofType('offer'), color: '#2f80ed' },
    {
      label: tr('Approved offers'),
      list: ofType('offer').filter((offer) => isOrderedStatus(offer.status)),
      color: '#27ae60',
    },
  ].map((step) => ({
    color: step.color,
    label: step.label,
    text: `${step.list.length} ${tr('pcs')} · ${shortEuros(
      step.list.reduce((total, offer) => total + getAmount(offer), 0)
    )}`,
    value: step.list.length,
  }));

  // The table of the chosen offers
  const sortValue = (offer: IOfferDashboardOffer): number | string => {
    switch (sortKey) {
      case 'amount':
        return getAmount(offer);
      case 'margin':
        return (
          getMarginRatio(offer.value ?? 0, offer.expense ?? 0) ?? -Infinity
        );
      case 'created':
        return offer.created;
      case 'departmentName':
        return departmentOf(offer);
      default:
        return `${offer[sortKey] ?? ''}`.toLowerCase();
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const first = sortValue(a);
    const second = sortValue(b);
    let order = 0;
    if (first < second) {
      order = -1;
    } else if (first > second) {
      order = 1;
    }
    return sortAscending ? order : -order;
  });
  const shown = showAll ? sorted : sorted.slice(0, 25);

  const sortArrow = sortAscending ? ' ▲' : ' ▼';

  // A win rate of 30 % or more is good
  let winRateColor = '#f2994a';
  if (winRate === null) {
    winRateColor = '#b8c0c8';
  } else if (winRate >= 30) {
    winRateColor = '#27ae60';
  }

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(
        key !== 'amount' && key !== 'created' && key !== 'margin'
      );
    }
  };

  const header = (key: SortKey, label: string, numeric = false) => (
    <th
      className={`${numeric ? 'numeric ' : ''}${
        sortKey === key ? 'sorted' : ''
      }`}
      onClick={() => sortBy(key)}
    >
      {label}
      {sortKey === key ? sortArrow : ''}
    </th>
  );

  const toggle = (current: null | string, value: string) =>
    current === value ? null : value;

  const kpis = [
    {
      color: '#2f80ed',
      label: tr('Offers'),
      note: tr('Opportunities count', { count: opportunities.length }),
      value: `${realOffers.length}`,
    },
    {
      color: '#6c3fb5',
      label: tr('Offered'),
      note: tr('VAT 0'),
      value: shortEuros(offeredValue),
    },
    {
      color: getMarginColor(marginRatio),
      label: tr('Margin'),
      note: euros(marginValue - marginExpense),
      value:
        marginRatio === null
          ? '–'
          : `${formatGroupedNumber(marginRatio, 1, 1)} %`,
    },
    {
      color: '#27ae60',
      label: tr('Approved'),
      note: tr('Approved count', { count: approved.length }),
      value: shortEuros(approvedValue),
    },
    {
      color: winRateColor,
      label: tr('Win rate'),
      note: tr('Win rate info'),
      value: winRate === null ? '–' : `${formatGroupedNumber(winRate, 0, 0)} %`,
    },
    {
      color: '#00a3a3',
      label: tr('Pipeline'),
      note: tr('Pipeline info'),
      value: shortEuros(pipeline),
    },
  ];

  const activeFilters = [
    department === null
      ? null
      : { label: department, clear: () => setDepartment(null) },
    type === null
      ? null
      : {
          label: t(`${translations.offers}:types.${type}`),
          clear: () => setType(null),
        },
    status === null
      ? null
      : {
          label: t(`${translations.offers}:statuses.${status}`),
          clear: () => setStatus(null),
        },
  ].filter((filter) => filter !== null) as {
    label: string;
    clear: () => void;
  }[];

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard">
        {tabs}
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters">
            {picker}
            <Input
              aria-label={tr('Department')}
              onChange={(event) =>
                setDepartment(
                  event.target.value === '' ? null : event.target.value
                )
              }
              type="select"
              value={department ?? ''}
            >
              <option value="">{tr('All departments')}</option>
              {departments.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Input>
            <Input
              aria-label={tr('Type')}
              onChange={(event) =>
                setType(event.target.value === '' ? null : event.target.value)
              }
              type="select"
              value={type ?? ''}
            >
              <option value="">{tr('All types')}</option>
              {TYPE_ORDER.map((offerType) => (
                <option key={offerType} value={offerType}>
                  {t(`${translations.offers}:types.${offerType}`)}
                </option>
              ))}
            </Input>
            <div className="offer-dashboard-segments" role="group">
              <button
                className={measure === 'amount' ? 'active' : ''}
                onClick={() => setMeasure('amount')}
                type="button"
              >
                {report.symbol}
              </button>
              <button
                className={measure === 'count' ? 'active' : ''}
                onClick={() => setMeasure('count')}
                type="button"
              >
                {tr('pcs')}
              </button>
            </div>
            {/* The currency of the sums, with the exchange rate below it */}
            <CurrencyToggle
              base={report.currency}
              onChange={report.setCurrency}
              rate={report.rate}
              value={report.currency}
            />
          </div>
        </div>

        {activeFilters.length > 0 ? (
          <div className="offer-dashboard-active-filters">
            {activeFilters.map((filter) => (
              <button key={filter.label} onClick={filter.clear} type="button">
                {filter.label} ✕
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <p className="offer-dashboard-info">{tr('Loading')}</p>
        ) : null}
        {failed ? (
          <p className="offer-dashboard-info">{tr('Load failed')}</p>
        ) : null}

        {data !== null && !loading ? (
          <>
            <div className="offer-dashboard-kpis">
              {kpis.map((kpi) => (
                <div
                  className="offer-dashboard-kpi"
                  key={kpi.label}
                  style={{ borderTopColor: kpi.color }}
                >
                  <div className="offer-dashboard-kpi-label">{kpi.label}</div>
                  <div
                    className="offer-dashboard-kpi-value"
                    style={{ color: kpi.color }}
                  >
                    {kpi.value}
                  </div>
                  <div className="offer-dashboard-kpi-note">{kpi.note}</div>
                </div>
              ))}
            </div>

            <div className="offer-dashboard-grid">
              <section className="offer-dashboard-card wide">
                <h2>
                  {tr(
                    `${byDay ? 'By day' : 'By month'} ${
                      measure === 'amount' ? 'amount' : 'count'
                    }`
                  )}
                </h2>
                <StackedColumns
                  columns={monthColumns}
                  format={formatMeasure}
                  series={statusSeries}
                />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By status')}</h2>
                <Donut
                  center={
                    measure === 'amount'
                      ? shortEuros(
                          statusParts.reduce(
                            (total, part) => total + part.value,
                            0
                          )
                        )
                      : `${forStatusDonut.length}`
                  }
                  centerLabel={
                    measure === 'amount' ? tr('Total') : tr('Offers')
                  }
                  onSelect={(key) => setStatus(toggle(status, key))}
                  parts={statusParts}
                  selected={status}
                />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By department')}</h2>
                <p className="offer-dashboard-card-info">
                  {tr('Margin badge info')}
                </p>
                <BarList
                  bars={departmentBars}
                  onSelect={(key) => setDepartment(toggle(department, key))}
                  selected={department}
                />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By work type')}</h2>
                <p className="offer-dashboard-card-info">
                  {tr('Work type info')}
                </p>
                {workTypeBars.length === 0 ? (
                  <p className="offer-dashboard-info">{tr('No data')}</p>
                ) : (
                  <BarList bars={workTypeBars} />
                )}
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('Funnel')}</h2>
                <p className="offer-dashboard-card-info">{tr('Funnel info')}</p>
                <Funnel steps={funnelSteps} />
              </section>
            </div>

            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>{tr('Offers of period', { count: filtered.length })}</h2>
              <div className="offer-dashboard-table-wrapper">
                <table className="offer-dashboard-table">
                  <thead>
                    <tr>
                      {header('targetName', tr('Target'))}
                      {header('customerName', tr('Customer'))}
                      {header('departmentName', tr('Department'))}
                      {header('type', tr('Type'))}
                      {header('status', tr('Status'))}
                      {header('created', tr('Created'), true)}
                      {header('amount', tr('Amount'), true)}
                      {header('margin', tr('Margin'), true)}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((offer) => {
                      const ratio =
                        offer.type === 'opportunity'
                          ? null
                          : getMarginRatio(
                              offer.value ?? 0,
                              offer.expense ?? 0
                            );

                      return (
                        <tr
                          key={offer.id}
                          onClick={() =>
                            history.push(`/offers/${offer.id}/${offer.type}`)
                          }
                        >
                          <td className="target">{offer.targetName}</td>
                          <td>{offer.customerName ?? ''}</td>
                          <td>
                            <span
                              className="dashboard-dot"
                              style={{
                                background:
                                  departmentColors[departmentOf(offer)],
                              }}
                            />
                            {departmentOf(offer)}
                          </td>
                          <td>
                            {t(`${translations.offers}:types.${offer.type}`)}
                          </td>
                          <td>
                            <span
                              className="offer-dashboard-status"
                              style={{
                                background:
                                  STATUS_COLORS[offer.status] ?? '#7f8c9a',
                              }}
                            >
                              {getOfferStatusLabel(t, offer.status, offer.type)}
                            </span>
                          </td>
                          <td className="numeric">
                            {new Date(offer.created).toLocaleDateString(
                              'fi-FI'
                            )}
                          </td>
                          <td className="numeric">
                            {offer.ndaRestricted
                              ? 'NDA'
                              : euros(getAmount(offer))}
                            {offer.type === 'opportunity' ? (
                              <span className="offer-dashboard-estimate">
                                {' '}
                                {tr('estimate')}
                              </span>
                            ) : null}
                          </td>
                          <td className="numeric">
                            {ratio === null ? (
                              '–'
                            ) : (
                              <span
                                className="offer-dashboard-margin"
                                style={{ borderColor: getMarginColor(ratio) }}
                              >
                                {`${formatGroupedNumber(ratio, 1, 1)} %`}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sorted.length > 25 ? (
                <button
                  className="offer-dashboard-show-all"
                  onClick={() => setShowAll(!showAll)}
                  type="button"
                >
                  {showAll
                    ? tr('Show less')
                    : tr('Show all', { count: sorted.length })}
                </button>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default OfferDashboard;
